import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { blogApiBase } from "./config";
import {
  type Blog,
  BlogImage,
  BlogMeta,
  excerptFor,
  formatDate,
  readingTime,
} from "./blogsShared";

// How many posts show initially, and how many each "Show more" click reveals.
const PAGE_SIZE = 5;

export default function Blogs() {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;

    const loadBlogs = async () => {
      try {
        const res = await fetch(`${blogApiBase()}/clients/new_dawn/blog`);
        if (!res.ok) {
          throw new Error(`Failed to load blogs: ${res.status}`);
        }
        const data: Blog[] = await res.json();
        if (!cancelled) setBlogs(data);
      } catch (error) {
        console.error("Error loading blogs:", error);
      }
    };

    loadBlogs();
    return () => {
      cancelled = true;
    };
  }, []);

  // The API returns posts newest-first; cap the list at visibleCount so the
  // page starts with the 5 most recent and "Show more" reveals 5 at a time.
  const [featured, ...rest] = blogs.slice(0, visibleCount);
  const hasMore = blogs.length > visibleCount;

  // Nothing to show (still loading, errored, or no published posts) — hide the
  // whole section rather than rendering an empty placeholder.
  if (!featured) return null;

  return (
    <section
      id="blogs"
      className="bg-[#f9fafb] px-6 py-16 md:px-10 md:py-28"
    >
      <div className="mx-auto w-full max-w-5xl">
        {/* heading */}
        <div className="space-y-4 text-center">
          <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
            Blogs…
          </h2>
          <p className="mx-auto max-w-xl text-base md:text-lg leading-relaxed font-trirong_light text-ink">
            Occasional writing on anxiety, attachment, self-care and the things
            that bring people to counselling.
          </p>
        </div>

        <div className="mt-12 md:mt-16">
          {/* featured / latest */}
          <article className="grid grid-cols-1 items-center gap-8 md:grid-cols-[1.1fr_0.9fr] md:gap-10">
                <Link
                  to={`/blogs/${featured.slug}`}
                  aria-label={featured.title}
                  className="block"
                >
                  <BlogImage
                    post={featured}
                    className="h-64 w-full rounded-2xl md:h-80"
                  />
                </Link>
                <div className="flex flex-col items-start">
                  <BlogMeta
                    parts={["Latest", readingTime(featured.body_html)]}
                    className="mb-4"
                  />
                  <h3 className="text-3xl md:text-4xl font-trirong_light leading-tight text-ink">
                    <Link to={`/blogs/${featured.slug}`}>
                      {featured.title}
                    </Link>
                  </h3>
                  <p className="mt-4 max-w-prose text-base leading-relaxed font-trirong_light text-ink-soft">
                    {excerptFor(featured)}
                  </p>
                  <Link
                    to={`/blogs/${featured.slug}`}
                    className="mt-7 rounded-full bg-sage px-7 py-3 font-nunito font-bold text-sm text-white transition-colors hover:bg-sage-deep"
                  >
                    Read article
                  </Link>
                </div>
              </article>

              {/* the rest */}
              {rest.length > 0 && (
                <div className="mt-12 grid grid-cols-1 gap-8 md:mt-16 md:grid-cols-2">
                  {rest.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blogs/${post.slug}`}
                      className="group flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5"
                    >
                      <BlogImage
                        post={post}
                        className="h-40 w-full flex-shrink-0 rounded-2xl sm:h-[130px] sm:w-[130px]"
                      />
                      <div>
                        <BlogMeta
                          parts={[
                            formatDate(post.created_at),
                            readingTime(post.body_html),
                          ]}
                          className="mb-2"
                        />
                        <h4 className="text-xl font-trirong_reg leading-snug text-ink transition-colors group-hover:text-sage-deep">
                          {post.title}
                        </h4>
                        <p className="mt-2 text-sm leading-relaxed font-trirong_light text-ink-soft">
                          {excerptFor(post, 130)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
          )}

          {/* reveal the next page of posts */}
          {hasMore && (
            <div className="mt-12 text-center md:mt-16">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="rounded-full bg-sage px-7 py-3 font-nunito font-bold text-sm text-white transition-colors hover:bg-sage-deep"
              >
                Show more…
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
